import * as React from "react";
import { Dispatch, useState } from "react";
import { Button, styled, Theme } from "@mui/material";
import { QualifiedFeature } from "fe-common/graphql";
import { DataInput, mergeResponseToQF } from "../../dataInputTypes";
import { PanelsUtil } from "../util/PanelsUtil";
import { CondensedTypography } from "fe-web-components/styles/typography";
import { getQFAndRefinementsLines } from "fe-common/logic";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { CheckboxButtonComponent } from "./CheckboxYesNoComponent";
import FilterListIcon from "@mui/icons-material/FilterList";
import { PositiveNegativeButtonComponent } from "./positiveNegativeButtonComponent";
//region [[ Styles ]]

const PositiveNegativePanelItemView = styled("div")(
  ({ theme, editfeature }: { theme?: Theme; editfeature: string }) => ({
    borderTop: "1px solid #e2e1e2",
    paddingRight: "5px",
    paddingLeft: "5px",
    backgroundColor: editfeature === "true" ? "#f5f8f8" : "unset",

    "&:last-child": {
      borderBottom: "1px solid #e2e1e2",
    },
    [theme.breakpoints.down(MobileView)]: {
      "&:last-child": {
        borderBottom: "none",
      },
      paddingRight: "0px",
      paddingLeft: "0px",
    },
  }),
);

const YesNoRow = styled("div")(({ theme }) => ({
  display: "flex",
  paddingBottom: "7px",
  paddingTop: "7px",
  [theme.breakpoints.down(MobileView)]: {
    paddingBottom: "14px",
    paddingTop: "14px",
  },
  justifyContent: "space-between",
  alignItems: "center",
}));

const RefineButtonContainer = styled("div")(({ theme }) => ({}));

const RefineButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  lineHeight: "0",
}));

const FeatureName = styled(CondensedTypography)(
  ({ theme, showmargin }: { theme?: any; showmargin: string }) => ({
    fontSize: "16px",
    marginLeft: showmargin === "true" ? "0px" : "30px",
    [theme.breakpoints.down(MobileView)]: {
      marginLeft: "5px",
    },
    fontWeight: 500,
    flex: 1,
    cursor: "pointer",
  }),
);

const YesNoContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "row",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    flexDirection: "row",
    justifyContent: "space-around",
    flex: 1,
  },
}));

const QualifiersText = styled("div")({
  fontSize: "13px",

  color: RosebudColorMap.Default,
});

const IconContainer = styled("div")({
  marginRight: "7px",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PositiveNegativePanelItemProps {
  className?: string;
  qf: QualifiedFeature;
  dataInput?: DataInput;
  onChange: Dispatch<DataInput>;
  isChiefComplaint: boolean;
  onYes?: () => void;
  onNo?: () => void;
  onDeselect?: () => void;
  onRefine: (qf: QualifiedFeature) => void;
  allowYesNo?: boolean;
  labLabels?: boolean;
  isThreeway?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PositiveNegativePanelItem = ({
  ...props
}: PositiveNegativePanelItemProps) => {
  const { qf, dataInput } = props;

  const [editFeature, setEditFeature] = useState(false);

  const featureName = PanelsUtil.featureToPanelText(qf);

  const qualifiersText =
    dataInput &&
    getQFAndRefinementsLines({
      ...qf,
      qualifiers: dataInput.qualifiers,
    })[1];

  const toggleYesNoRefine = (event) => {
    if (!dataInput) {
      props.onYes();
      // If there are qualifiers a click on feature name should open refine pop-up
    } else if (dataInput?.qualifiers?.length && props.onRefine) {
      onRefine();
    } else if (dataInput.qualifiers.length === 0) {
      props.onDeselect();
    }
  };

  const onRefine = () => {
    props.onRefine(mergeResponseToQF(qf, dataInput));
  };

  const toggleYesNoCheckBox = (dataInput) => {
    if (props.isThreeway === true) {
      if (dataInput && dataInput.negate === false) {
        props.onYes();
      } else if (dataInput && dataInput.negate === true) {
        props.onNo();
      } else {
        setEditFeature(false);
        props.onDeselect();
      }
    } else {
      if (dataInput && dataInput.negate === false) {
        props.onYes();
      } else {
        setEditFeature(false);
        props.onDeselect();
      }
    }
  };

  const toggleYesNoButton = (dataInput) => {
    if (!dataInput && props.labLabels === true) {
      props.onDeselect();
    } else if (dataInput && dataInput.negate === false) {
      props.onYes();
    } else {
      setEditFeature(false);
      props.onNo();
    }
  };

  const getNegateText = () => {
    if (props.labLabels) {
      return dataInput
        ? dataInput.negate === true
          ? "Negative"
          : "Positive"
        : "";
    } else {
      return dataInput && dataInput.negate === true ? "No" : "";
    }
  };

  return (
    <PositiveNegativePanelItemView
      className={props.className}
      editfeature={editFeature ? "true" : "false"}
    >
      <YesNoRow>
        <YesNoContainer>
          {!props.labLabels && (
            <CheckboxButtonComponent
              disabled={props.isChiefComplaint}
              qf={props.qf}
              isThreeway={props.isThreeway}
              onChange={toggleYesNoCheckBox}
              dataInput={props.dataInput}
            />
          )}
          <FeatureName
            onClick={toggleYesNoRefine}
            showmargin={props.labLabels.toString()}
          >
            {getNegateText()} {featureName}
            <QualifiersText>{qualifiersText}</QualifiersText>
          </FeatureName>
          {props.dataInput && props.onRefine && (
            <RefineButtonContainer>
              <RefineButton
                variant={"outlined"}
                color={"primary"}
                onClick={onRefine}
              >
                <IconContainer>
                  <FilterListIcon />
                </IconContainer>
                Refine
              </RefineButton>
            </RefineButtonContainer>
          )}
          {(props.labLabels || props.dataInput) &&
            props.allowYesNo === true && (
              <PositiveNegativeButtonComponent
                qf={props.qf}
                allowDeselect={props.labLabels === true ? true : false}
                onChange={toggleYesNoButton}
                response={props.dataInput}
                disabled={false}
                labLabels={props.labLabels}
              />
            )}
        </YesNoContainer>
      </YesNoRow>
    </PositiveNegativePanelItemView>
  );
};
